/**
 * Server-side reCAPTCHA verification utility
 */

export interface RecaptchaConfig {
  secretKey: string;
  minimumScore?: number; // For reCAPTCHA v3, minimum score (0.0 to 1.0)
  timeoutMs?: number; // Request timeout in milliseconds
}

export interface RecaptchaVerificationResult {
  success: boolean;
  score?: number; // reCAPTCHA v3 score
  action?: string; // reCAPTCHA v3 action
  challengeTimestamp?: string;
  hostname?: string;
  errorCodes?: string[];
}

/**
 * Verify reCAPTCHA token with Google's API
 */
export async function verifyRecaptcha(
  token: string,
  config: RecaptchaConfig
): Promise<RecaptchaVerificationResult> {
  if (!config.secretKey) {
    console.warn('RECAPTCHA_SECRET_KEY not configured, skipping verification');
    return { success: true };
  }

  if (!token || typeof token !== 'string') {
    return {
      success: false,
      errorCodes: ['missing-input-response']
    };
  }

  const timeout = config.timeoutMs || 10000; // 10 second default timeout

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    const response = await fetch('https://www.google.com/recaptcha/api/siteverify', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        secret: config.secretKey,
        response: token
      }),
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      console.error('reCAPTCHA API returned non-OK status:', response.status);
      return {
        success: false,
        errorCodes: ['recaptcha-api-error']
      };
    }

    const data = await response.json();
    
    // Check minimum score for v3 (if configured)
    if (config.minimumScore !== undefined && data.score !== undefined) {
      if (data.score < config.minimumScore) {
        return {
          success: false,
          score: data.score,
          errorCodes: ['score-threshold-not-met']
        };
      }
    }

    return {
      success: data.success === true,
      score: data.score,
      action: data.action,
      challengeTimestamp: data.challenge_ts,
      hostname: data.hostname,
      errorCodes: data['error-codes'] || []
    };

  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      console.error('reCAPTCHA verification timeout');
      return {
        success: false,
        errorCodes: ['timeout-or-duplicate']
      };
    }

    console.error('reCAPTCHA verification error:', error);
    return {
      success: false,
      errorCodes: ['network-error']
    };
  }
}

/**
 * Get human-readable error message for reCAPTCHA error codes
 */
export function getRecaptchaErrorMessage(errorCodes: string[]): string {
  const errorMessages: { [key: string]: string } = {
    'missing-input-secret': 'reCAPTCHA secret key is missing',
    'invalid-input-secret': 'reCAPTCHA secret key is invalid',
    'missing-input-response': 'reCAPTCHA token is missing',
    'invalid-input-response': 'reCAPTCHA token is invalid or malformed',
    'bad-request': 'The request is invalid or malformed',
    'timeout-or-duplicate': 'reCAPTCHA verification timed out or token was already used',
    'score-threshold-not-met': 'reCAPTCHA score is below the required threshold',
    'recaptcha-api-error': 'reCAPTCHA service is unavailable',
    'network-error': 'Network error during reCAPTCHA verification'
  };

  if (!errorCodes || errorCodes.length === 0) {
    return 'reCAPTCHA verification failed';
  }

  const knownErrors = errorCodes
    .filter(code => errorMessages[code])
    .map(code => errorMessages[code]);

  return knownErrors.length > 0 
    ? knownErrors.join(', ')
    : 'reCAPTCHA verification failed';
}